import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Storage } from "@ionic/storage";
import { Observable } from "rxjs";
import { CookieService } from "ngx-cookie-service";
import { UtilityProvider } from "../../providers/utility/utility";
import { FacilityServicesProvider } from "../../providers/facility-service/facility-service";
import { EnvironmentServiceProvider } from "../../providers/environment-service/environment-service";
import { OauthCodeFlowService } from "../oauth-code-flow-service/oauth-code-flow-service";

@Injectable()
export class InterceptorAuthProvider {
  public token: any;
  isLoggedIn: boolean = false;
  // isTokenCurrent indicates that the token has been renewed for
  // the current app instance and it has the up to date claims
  isTokenCurrent: boolean = false;

  constructor(
    public utilityProvider: UtilityProvider,
    public facilityService: FacilityServicesProvider,
    public storage: Storage,
    public http: HttpClient,
    public cookieService: CookieService,
    public environmentService: EnvironmentServiceProvider,
    private oauthCodeFlow: OauthCodeFlowService
  ) {}

  doLogin(userName: string, password: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/x-www-form-urlencoded",
      }),
    };

    let body: string =
      "client_id=mtmclient" +
      "&username=" +
      encodeURIComponent(userName) +
      "&password=" +
      encodeURIComponent(password) +
      "" +
      "&grant_type=password" +
      "&scope=" +
      encodeURIComponent("openid profile");

    return this.http.post(
      this.environmentService.env.apiEndpoint + "/idp/connect/token",
      body,
      httpOptions
    );
  }

  private urlBase64Decode(str: string) {
    var output = str.replace(/-/g, "+").replace(/_/g, "/");
    switch (output.length % 4) {
      case 0: {
        break;
      }
      case 2: {
        output += "==";
        break;
      }
      case 3: {
        output += "=";
        break;
      }
      default: {
        throw "Illegal base64url string!";
      }
    }
    return decodeURIComponent(window.atob(output));
  }

  public decodeToken(token: string) {
    if (token == null) {
      throw new Error("Token is null");
    }
    var parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error("JWT must have 3 parts");
    }
    var decoded = this.urlBase64Decode(parts[1]);
    if (!decoded) {
      throw new Error("Cannot decode the token");
    }
    return JSON.parse(decoded);
  }

  logInAuth(loggedIn) {
    this.isLoggedIn = loggedIn;
  }

  async logout() {
    this.isLoggedIn = false;
    this.storage.remove("filterMember");
    this.storage.remove("paginationStorage");
    this.storage.remove("clientPlanIDs");
    this.storage.remove("clientPlans");
    this.storage.remove("formValues");
    this.storage.remove("filterTrip");
    this.storage.remove("tripPagination");
    sessionStorage.removeItem("savedMember");
    localStorage.removeItem("SMPinactive");
    sessionStorage.clear();
    if (this.environmentService.isCST) {
      await this.oauthCodeFlow.logout();
    } else {
      window.location.href = `${this.environmentService.env.webUrl}/link`;
      // window.location.href = "localhost:8100/link";
    }
  }

  returnToLink() {
    window.location.href = `${this.environmentService.env.webUrl}/link/#/home`;
    // window.location.href = "localhost:8100/link/#/home";
  }

  /**
   * Getter for access token
   **/
  getLinkToken() {
    if (this.environmentService.isCST) {
      const user = this.oauthCodeFlow.user;
      return user ? user.access_token : null;
    } else {
      return sessionStorage.getItem("linkToken");
    }
  }
  /**
   * Getter for the decrypted link token result
   */
  getLinkTokenResult() {
    const token = this.getLinkToken();
    return token ? this.decodeToken(token) : null;
  }

  async isAuthenticated() {
    if (this.environmentService.isCST) {
      let user = await this.oauthCodeFlow.getUser();
      if (user) {
        if (!this._isUserValid(user)) {
          user = await this.renewToken();
        }
      } else if (this.oauthCodeFlow.isIdpRedirect) {
        try {
          user = await this.oauthCodeFlow.signinRedirectCallback();
          if (user) {
            if (!this._isUserValid(user)) {
              user = await this.renewToken();
            }
            this.oauthCodeFlow.activeBrowserSession();
          }
        } catch (error) {
          // Handle the scenario where the redirect state is not found
          // Returning false will force signRedirect and a new state will be generated
          console.log(
            "interceptor-auth.ts isAuthenticated() signinRedirectCallback error: ",
            error
          );
          return false;
        }
      }
      console.log("interceptor-auth.ts isAuthenticated() user: ", user);
      return !!(
        this.oauthCodeFlow.isActiveBrowserSession() &&
        this._isUserValid(user) &&
        this.checkUserRole()
      );
    } else {
      const linkTokenResult = this.getLinkTokenResult();
      let isExpired =
        linkTokenResult !== null && linkTokenResult.exp * 1000 < Date.now();
      return !isExpired && this.checkUserRole();
    }
  }

  checkUserRole() {
    const tokenResult = this.getLinkTokenResult();
    return !!(
      tokenResult &&
      (tokenResult.UserRole === "Facility" ||
        tokenResult.UserRole === "Admin" ||
        tokenResult.UserRole === "Client" ||
        tokenResult.UserRole === "Member")
    );
  }

  /**
   * Verifies that the user's role and application allows to request a trip (ETF)\
   * @returns {boolean}
   */
  isRequestTripEnabled() {
    const linkTokenResult = this.getLinkTokenResult();
    return (
      linkTokenResult &&
      (linkTokenResult.UserRole === "Client" ||
        (linkTokenResult.Application.includes("intake") &&
          (linkTokenResult.UserRole === "Admin" ||
            linkTokenResult.UserRole === "Facility" ||
            linkTokenResult.UserRole === "CustomerCareRep" ||
            linkTokenResult.UserRole === "Member")))
    );
  }

  setLinkTokenCookie() {
    this.cookieService.delete("linkToken");
    const token = this.getLinkToken();
    // The cookie expires with the browser's session
    this.cookieService.set(
      "linkToken",
      token,
      0,
      "/",
      this.environmentService.env.domain
    );
  }

  goToEtf() {
    window.location.href = this.environmentService.env.etfUrl;
  }

  goToESTClientEtf() {
    window.open(this.environmentService.env.etfUrl, "_blank");
  }

  getUser(): Observable<any> {
    if (this.environmentService.isCST) {
      return this.oauthCodeFlow.getUserObservable();
    } else {
      return Observable.of(this.getLinkTokenResult());
    }
  }

  async signIn() {
    if (this.environmentService.isCST) {
      console.log("interceptor-auth.ts sign()", "Redirect to IDP for login");
      return this.oauthCodeFlow.signinRedirect();
    } else {
      window.location.href = `${this.environmentService.env.webUrl}/link`;
    }
  }

  async renewToken() {
    try {
      console.log("interceptor-auth.ts renewToken called");
      const user = await this.oauthCodeFlow.renewToken();
      this.isTokenCurrent = true;
      return user;
    } catch (error) {
      console.log("interceptor-auth.ts renewToken() failed:", error);
    }
  }

  private _isUserValid(user): boolean {
    return user && !user.expired && this.isTokenCurrent;
  }

  setFacilityName(tokenResults) {
    if (tokenResults.UserRole == "Facility") {
      this.facilityService
        .getFacility(tokenResults.facilityId)
        .subscribe((res) => {
          sessionStorage.setItem("facilityName", res.description);
        });
    }
  }

  getRootPageInfo(tokenResults) {
    let rootPage = "MemberSearchPage";
    let rootParams = null;

    if (tokenResults != null) {
      const userRole = tokenResults.UserRole;
      if (userRole == "Admin") {
        rootPage = "FacilityPage";
      } else if (userRole == "Facility") {
        rootParams = { facilityId: tokenResults.facilityId };
        rootPage = "FacilityTripsPage";
        console.log("logging user role of facility HERE", userRole);
      } else if (userRole == "Client") {
        rootPage = "MemberSearchPage";
      }
    }

    return {
      rootPage,
      rootParams,
    };
  }
}



// WEBPACK FOOTER //
// ./src/providers/interceptor-auth/interceptor-auth.ts